import type { MediaData } from '../shared/media.types';
import type { ActionMessage } from '../types';
import { invokeAction } from './invoke';

export interface OpenPhotoCollectionProps {
  readonly noPermissionsData?: NoPermissionsData;
  readonly steps: PhotoCollectionStep[];
  readonly analyticsParams?: Record<string, unknown>;
  readonly attachmentData: AttachmentData;
}

interface AttachmentData {
  readonly resourceId?: string;
  readonly attachmentType?: string;
  readonly type?: 'image';
  readonly scriptId?: string;
  readonly questionId?: string;
  readonly context?: 'claim' | 'policy' | 'quote';
  readonly product?: 'car' | 'crypto' | 'home' | 'life' | 'pet';
}

interface PhotoCollectionStep {
  readonly id: string;
  readonly supportMultiplePhotos?: boolean;
  readonly photoLimitWarning?: PhotoLimitWarning;
  readonly initialAction?: PhotoCollectionActionContent;
  readonly informationData: InformationData;
  readonly closeAction?: PhotoCollectionActionContent;
  readonly imageFormatData?: ImageFormatData;
  readonly previewInformationData?: PreviewInformationData;
  readonly uploadData?: Record<string, string>;
  readonly analyticsParams?: Record<string, unknown>;
}

interface NoPermissionsData {
  readonly title: string;
  readonly body: string;
  readonly positiveAction: string;
  readonly negativeAction: string;
}

interface PhotoLimitWarning {
  readonly title?: string;
  readonly subtitle?: string;
  readonly limit?: number;
}

interface InformationData {
  readonly title?: string;
  readonly mediaData?: MediaData;
  readonly action?: PhotoCollectionActionContent;
}

interface ImageFormatData {
  readonly type: string;
  readonly data: {
    readonly quality: number;
    readonly width: number;
    readonly height: number;
  };
}

interface PreviewInformationData {
  readonly title: string;
  readonly subtitle: string;
  readonly retakeButton: {
    readonly caption: string;
  };
  readonly saveButton: {
    readonly caption: string;
  };
}

interface Button {
  readonly style: 'ceruleanBlue' | 'gray' | 'pink' | 'pinkWithShadow' | 'turquoise';
  readonly caption: string;
  readonly action: PhotoCollectionActionContent;
}

interface BaseAction {
  readonly type: ActionType;
  readonly enabled: boolean;
  readonly caption?: string;
  readonly analyticsParams?: Record<string, unknown>;
}

type ActionType = 'back' | 'backToPrevious' | 'informationBottomSheet' | 'noAction';
type Action<T extends ActionType, TData = {}> = BaseAction & TData & { readonly type: T };

type PhotoCollectionActionContent =
  | Action<'back'>
  | Action<'backToPrevious'>
  | Action<'informationBottomSheet', { readonly data: InformationBottomSheetActionData }>
  | Action<'noAction'>;

// Information Bottom Sheet Data
interface InformationBottomSheetActionData {
  readonly title?: string;
  readonly mediaDescription?: string;
  readonly richDescription?: { readonly text: string };
  readonly mediaData?: MediaData;
  readonly hasTransparentBackground?: boolean;
  readonly shouldShowDismissButton?: boolean;
  readonly analyticsParams?: Record<string, unknown>;
  readonly buttons?: Button[];
  readonly buttonsOrientation?: 'horizontal' | 'vertical';
  readonly style?: 'big' | 'normal';
  readonly continuousAnimation?: boolean;
  readonly backButtonData?: BackButtonData;
  readonly backwardMedia?: MediaData;
  readonly showCloseButton?: boolean;
}

interface BackButtonData {
  readonly mediaData?: MediaData;
  readonly action?: PhotoCollectionActionContent;
  readonly analyticsParams?: Record<string, unknown>;
}

export type OpenPhotoCollectionAction = ActionMessage<'openPhotoCollection', OpenPhotoCollectionProps>;
export const openPhotoCollection = invokeAction('uniclientAction', 'openPhotoCollection');
